import * as arrow from "apache-arrow";
import { Box } from "@mui/material";
import {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import { useCustomer, useWasm } from "../../../contexts/app/provider";
import dayjs from "../../../utils/dayjs";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { SupplyWalkEditsContext } from "../../../contexts/supply_walk_edits/provider";

import { DatePivotParams } from "../../../entities/date_pivot_params";
import { ErrorState } from "./ErrorState";
import { LoadingState } from "./LoadingState";
import { paramsToTransformArgs as defaultParamsToTransformArgs } from "../../../utils/params_to_transform_args";
import {
  parseParquetData,
  parseParquetDataWithRefresh,
  ParsedParquetData,
  FullParsedParquetData,
  ChunkedParsedParquetData,
} from "../../../utils/parquet";
import { useChunkedParquetRowProvider } from "../../../utils/chunked_parquet_provider";
import {
  PivotViewConfig,
  type RenderedViewBinding,
} from "../../../entities/pivot_view_config";
import { ChartConfigProp } from "../../../entities/chart_config";
import { UniversalTableViewer, TableType } from "../../universal_mui_table";
import { UniversalChartViewer } from "../../universal_mui_chart";
import { PoCellPopover } from "../../po_cell_popover";
import { TransferCellPopover } from "../../transfer_cell_popover";
import { UnfulfilledDemandCellPopover } from "../../unfulfilled_demand_cell_popover";
import { ForecastedSalesCellPopover } from "../../forecasted_sales_cell_popover";
import { SalesOrderCellPopover } from "../../sales_order_cell_popover";
import { usePerspective } from "../../../contexts/app/provider";
import {
  GetTransformedParquetDownloadUrlDocument,
  GetExportCsvDownloadUrlDocument,
  GetMatrixSortKeysDocument,
  useGetTransformedRawParquetSubscription,
} from "../../../services/perspective_api/__generated__/perspective_server-types";
import { Run, RunSettings } from "../../../services/appsync/types";
import { AuthenticationService } from "../../../services/perspective_api/authentication";
import type { BackendExportFn } from "../index";

const RENDERED_VIEW_RECEIPT_ID = /^rvr[123]_[a-f0-9]{64}$/;

const hasNoSelectedFilterValues = (filter: unknown): boolean =>
  Array.isArray(filter) && Array.isArray(filter[2]) && filter[2].length === 0;

function ChunkedTableViewer<T extends DatePivotParams = DatePivotParams>({
  chunkedConfig,
  params,
  viewControllerConfig,
  tableType,
  columnHeaderMap,
  columnFormatters,
  sortConfig,
  setSortConfig,
  columnSchema,
  expandedGroups,
  setExpandedGroups,
  TableViewer,
  fetchSortKeys,
  renderedSource,
}: {
  chunkedConfig: {
    downloadUrl: string;
    parquetRowCount: number;
    getRefreshUrl: () => Promise<string | null>;
  };
  params: T;
  viewControllerConfig: PivotViewConfig<T>;
  tableType?: string;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  TableViewer: React.ComponentType<any>;
  fetchSortKeys?: (measure: string) => Promise<FullParsedParquetData | null>;
  renderedSource?: RenderedViewBinding;
}) {
  const { rowCount, getRow, columns, isColumnsReady } =
    useChunkedParquetRowProvider({
      downloadUrl: chunkedConfig.downloadUrl,
      totalRowCount: chunkedConfig.parquetRowCount,
      getRefreshUrl: chunkedConfig.getRefreshUrl,
    });

  if (!isColumnsReady || !columns || columns.length === 0) {
    return <LoadingState />;
  }

  const chunkedParsedData: ParsedParquetData = {
    chunked: true,
    rowCount,
    getRow,
    columns,
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableViewer
        parsedData={chunkedParsedData}
        loading={false}
        error={null}
        viewParams={params}
        viewControllerConfig={viewControllerConfig}
        tableType={tableType as TableType}
        columnHeaderMap={columnHeaderMap}
        columnFormatters={columnFormatters}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        columnSchema={columnSchema}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        fetchSortKeys={fetchSortKeys}
        renderedSource={renderedSource}
      />
    </Box>
  );
}

export const TransformedCustomViewViewerContainer = <
  T extends DatePivotParams = DatePivotParams,
>({
  params,
  runSettings,
  renderedRun,
  comparedToRunSettings,
  viewControllerConfig,
  viewerComponent: ViewerComponent,
  tableType,
  columnHeaderMap,
  columnFormatters,
  parsedData,
  setParsedData,
  isChartView,
  sortConfig,
  setSortConfig,
  chartConfig,
  ChartComponent,
  columnSchema,
  expandedGroups,
  setExpandedGroups,
  setBackendExportFn,
  setInMemoryArrowTable,
}: {
  params: T;
  runSettings: RunSettings | null;
  renderedRun: Run | null;
  comparedToRunSettings: RunSettings | null;
  viewControllerConfig: PivotViewConfig<T>;
  viewerComponent: React.ComponentType<{
    parsedData: ParsedParquetData | null;
    loading: boolean;
    error: string | null;
    viewParams: T;
    tableType?: string;
    columnHeaderMap?: Record<string, string>;
    columnFormatters?: Record<
      string,
      (value: any, row: any) => React.ReactNode
    >;
    columnSchema?: Record<
      string,
      {
        dtype: string;
        min?: string;
        max?: string;
        unique_values?: string[];
        value_counts?: Record<string, number>;
      }
    >;
    renderedSource?: RenderedViewBinding;
  }> | null;
  tableType?: string;
  columnHeaderMap?: Record<string, string>;
  columnFormatters?: Record<string, (value: any, row: any) => React.ReactNode>;
  parsedData: ParsedParquetData | null;
  setParsedData: (data: ParsedParquetData | null) => void;
  isChartView?: boolean;
  sortConfig: Array<{ key: string; direction: "asc" | "desc"; index: number }>;
  setSortConfig: React.Dispatch<
    React.SetStateAction<
      Array<{ key: string; direction: "asc" | "desc"; index: number }>
    >
  >;
  chartConfig: ChartConfigProp;
  ChartComponent?: React.ComponentType<any>;
  columnSchema: Record<
    string,
    {
      dtype: string;
      min?: string;
      max?: string;
      unique_values?: string[];
      value_counts?: Record<string, number>;
    }
  >;
  expandedGroups?: Set<string>;
  setExpandedGroups?: React.Dispatch<React.SetStateAction<Set<string>>>;
  setBackendExportFn?: React.Dispatch<
    React.SetStateAction<BackendExportFn | null>
  >;
  setInMemoryArrowTable?: (table: arrow.Table | null) => void;
}) => {
  const { datasetName, transformId, folder, paramsToTransformArgs } =
    viewControllerConfig;
  const chunkedMatrixTransformId = (
    viewControllerConfig as { chunkedMatrixTransformId?: string }
  )?.chunkedMatrixTransformId;
  const receiptTransformId = viewControllerConfig.receiptTransformId;
  const { perspectiveApiService } = usePerspective();
  const { selectedCustomerId } = useCustomer();
  const { mainRun, comparedRun, lastSuccessfulRun } =
    useContext(VersionedDataContext);
  const activeRun = mainRun ?? lastSuccessfulRun;
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [chunkedConfig, setChunkedConfig] = useState<{
    downloadUrl: string;
    parquetRowCount: number;
    getRefreshUrl: () => Promise<string | null>;
    requestKey: string;
    renderedSource?: RenderedViewBinding;
  } | null>(null);
  const { wasmInitialized, wasmError } = useWasm();
  const [isSubscriptionSkipped, setIsSubscriptionSkipped] = useState(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();
  const inMemoryMatrixTableRef = useRef<arrow.Table | null>(null);
  const loadGenerationRef = useRef(0);
  const [renderedRequestKey, setRenderedRequestKey] = useState<string | null>(
    null,
  );
  const [renderedSource, setRenderedSource] =
    useState<RenderedViewBinding | null>(null);
  const [receiptBinding, setReceiptBinding] = useState<{
    requestKey: string;
    receiptId: string;
  } | null>(null);

  const effectiveParams = useMemo(() => {
    // Drop in-progress "Filter by" cards that have no value yet: a freshly
    // added ["col","in",[]] stays in params so its card still renders, but it
    // must reach neither the transform nor metadata describing its rows.
    const filters = params.filters.filter(
      (filter) => !hasNoSelectedFilterValues(filter),
    );
    const postFilters = params.postFilters?.filter(
      (filter) => !hasNoSelectedFilterValues(filter),
    );
    if (
      filters.length === params.filters.length &&
      postFilters?.length === params.postFilters?.length
    ) {
      return params;
    }
    return {
      ...params,
      filters,
      ...(postFilters ? { postFilters } : {}),
    } as T;
  }, [params]);

  const computeArgsJson = useCallback(() => {
    return paramsToTransformArgs
      ? paramsToTransformArgs(effectiveParams)
      : defaultParamsToTransformArgs(effectiveParams);
  }, [effectiveParams, paramsToTransformArgs]);

  // Identity-stable args: adding/removing an EMPTY in-progress filter leaves
  // the effective args deep-equal (empty filters are dropped above), but a
  // recomputed object would still be a NEW reference — and that alone made
  // the subscription variables change, tearing down and re-firing the whole
  // (slow) transform subscription. Key the object on its serialized form so
  // deep-equal args keep the same reference and no refetch fires.
  const argsJsonString = useMemo(
    () => JSON.stringify(computeArgsJson()),
    [computeArgsJson],
  );
  const argsJson = useMemo(() => JSON.parse(argsJsonString), [argsJsonString]);

  /** Use matrix transform from the start when configured, so we don't wait for the long-format transform first. */
  const effectiveTransformId = chunkedMatrixTransformId ?? transformId;

  // Re-subscribe when an approval save invalidates the server cache. Apollo
  // strips unknown top-level variables before diffing, so adding a sibling
  // field (like _subscriptionKey) doesn't actually trigger a new subscription.
  // Instead we inject the reloadVersion inside `params` — that IS a declared
  // subscription variable, so Apollo forwards it and sees a changed shape.
  // The server's pydantic args model ignores unknown keys, so the transform
  // is unaffected. SupplyWalkEditsContext is only provided on the PO Inbox
  // route; elsewhere this is null and reloadVersion stays 0.
  const supplyWalkEdits = useContext(SupplyWalkEditsContext);
  const reloadVersion = supplyWalkEdits?.reloadVersion ?? 0;

  const subscriptionVariables = useMemo(() => {
    const paramsWithReload =
      reloadVersion > 0
        ? { ...argsJson, __reloadVersion: reloadVersion }
        : argsJson;
    const vars = {
      datasetName,
      transformId: effectiveTransformId,
      folder,
      runSettingsJson: runSettings ?? null,
      comparedToRunSettingsJson: comparedToRunSettings ?? null,
      format: "parquet" as const,
      params: paramsWithReload,
    };
    return vars;
  }, [
    datasetName,
    effectiveTransformId,
    folder,
    runSettings,
    comparedToRunSettings,
    argsJson,
    reloadVersion,
  ]);

  // This key describes the request whose rows may be rendered. Tenant is
  // explicit because a server-latest request otherwise has identical GraphQL
  // variables across a ROOT user's customer switch.
  const requestKey = useMemo(
    () =>
      JSON.stringify({
        selectedCustomerId,
        variables: subscriptionVariables,
      }),
    [selectedCustomerId, subscriptionVariables],
  );
  const currentRequestKeyRef = useRef(requestKey);
  currentRequestKeyRef.current = requestKey;

  const receiptSubscriptionVariables = useMemo(
    () => ({
      ...subscriptionVariables,
      transformId: receiptTransformId ?? effectiveTransformId,
    }),
    [effectiveTransformId, receiptTransformId, subscriptionVariables],
  );
  const receiptRequestKey = useMemo(
    () =>
      JSON.stringify({
        selectedCustomerId,
        variables: receiptSubscriptionVariables,
      }),
    [receiptSubscriptionVariables, selectedCustomerId],
  );
  const currentReceiptRequestKeyRef = useRef(receiptRequestKey);
  currentReceiptRequestKeyRef.current = receiptRequestKey;

  const beginLoadGeneration = useCallback(
    (eventId: string, receiptId: unknown) => {
      const token = loadGenerationRef.current + 1;
      loadGenerationRef.current = token;
      const acceptedReceiptId =
        typeof receiptId === "string" &&
        RENDERED_VIEW_RECEIPT_ID.test(receiptId)
          ? receiptId
          : undefined;
      return {
        requestKey,
        token,
        renderedSource: renderedRun
          ? Object.freeze({
              generation: `${token}:${eventId}`,
              run: renderedRun,
              ...(acceptedReceiptId ? { receiptId: acceptedReceiptId } : {}),
            })
          : undefined,
      };
    },
    [renderedRun, requestKey],
  );

  const isCurrentLoadGeneration = useCallback(
    (load: Readonly<{ requestKey: string; token: number }>) =>
      load.token === loadGenerationRef.current &&
      load.requestKey === currentRequestKeyRef.current,
    [],
  );

  // Always use UniversalTableViewer as the default viewerComponent if no viewerComponent is provided
  const TableViewer = ViewerComponent || UniversalTableViewer;
  // Same for chart viewer
  const ChartViewer = ChartComponent || UniversalChartViewer;

  // Cleanup effect for retry timeout
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  // Listen for token refresh events to clear subscription skip state
  useEffect(() => {
    const handleTokenRefresh = () => {
      console.info("Token refreshed, clearing subscription skip state");
      setIsSubscriptionSkipped(false);
    };

    // Registry, not the single slot: several views and the perspective
    // service all listen, and none may evict another.
    return AuthenticationService.addOnTokenRefresh(handleTokenRefresh);
  }, []);

  useGetTransformedRawParquetSubscription({
    client: perspectiveApiService.getClient(),
    variables: subscriptionVariables,
    onData: (data: any) => {
      // An observable from the previous variables can finish while Apollo is
      // replacing it. Never let that event mutate the new request's state.
      if (currentRequestKeyRef.current !== requestKey) return;

      const payload = data.data?.data?.getTransformedRawParquet;
      const status = payload?.status;
      const parquetDataString = payload?.parquetData;
      const errorMessage = payload?.errorMessage;

      if (status === "COMPLETED" && parquetDataString) {
        const load = beginLoadGeneration(
          String(payload?.id ?? "completed"),
          payload?.renderedViewReceiptId,
        );
        const isBase64 = !parquetDataString.startsWith("http");
        const parquetRowCount = payload?.parquetRowCount ?? null;
        setError(null);

        const refreshVariables = {
          datasetName,
          transformId: effectiveTransformId,
          folder,
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: comparedToRunSettings ?? null,
          params: argsJson,
        };
        const getRefreshUrl = async () => {
          const result = await perspectiveApiService.getClient().query({
            query: GetTransformedParquetDownloadUrlDocument,
            variables: refreshVariables,
          });
          return (
            result.data?.getTransformedParquetDownloadUrl?.downloadUrl ?? null
          );
        };

        if (
          !isBase64 &&
          parquetRowCount != null &&
          parquetRowCount > 0 &&
          chunkedMatrixTransformId != null
        ) {
          if (!isCurrentLoadGeneration(load)) return;
          console.log(
            "[transformed view] using chunked parquet: URL + rowCount =",
            parquetRowCount.toLocaleString(),
          );
          inMemoryMatrixTableRef.current = null;
          setInMemoryArrowTable?.(null);
          setChunkedConfig({
            downloadUrl: parquetDataString,
            parquetRowCount,
            getRefreshUrl,
            requestKey: load.requestKey,
            renderedSource: load.renderedSource,
          });
          setRenderedRequestKey(null);
          setRenderedSource(null);
          setParsedData(null);
          setLoading(false);
        } else {
          console.log(
            "[transformed view] using in-memory parquet (chunked path not used: server sent",
            isBase64 ? "base64" : "URL without parquetRowCount",
            ")",
          );
          setChunkedConfig(null);
          setLoading(true);
          const parsePromise = isBase64
            ? parseParquetData(parquetDataString, true)
            : parseParquetDataWithRefresh(parquetDataString, getRefreshUrl);

          parsePromise
            .then((parsed) => {
              if (!isCurrentLoadGeneration(load)) return;
              if (chunkedMatrixTransformId && "arrowTable" in parsed) {
                // Matrix transform returned base64 (small dataset). Wrap the
                // in-memory arrow table as ChunkedParsedParquetData so the
                // matrix viewer in UniversalTableViewer handles it correctly.
                const full = parsed as FullParsedParquetData;
                inMemoryMatrixTableRef.current = full.arrowTable;
                setInMemoryArrowTable?.(full.arrowTable);
                const wrapped: ChunkedParsedParquetData = {
                  chunked: true,
                  rowCount: full.arrowTable.numRows,
                  getRow: (idx: number) => {
                    const row: Record<string, unknown> = {};
                    for (const field of full.arrowTable.schema.fields) {
                      row[field.name] = full.arrowTable
                        .getChild(field.name)
                        ?.get(idx);
                    }
                    return row;
                  },
                  columns: full.columns,
                };
                console.log(
                  "[transformed view] matrix base64 → wrapped as chunked, rows:",
                  full.arrowTable.numRows,
                );
                setParsedData(wrapped);
              } else {
                inMemoryMatrixTableRef.current = null;
                setInMemoryArrowTable?.(null);
                setParsedData(parsed);
              }
              setRenderedRequestKey(load.requestKey);
              setRenderedSource(load.renderedSource ?? null);
              setLoading(false);
            })
            .catch((err) => {
              if (!isCurrentLoadGeneration(load)) return;
              console.error("[transformed view] parse failed:", err);
              inMemoryMatrixTableRef.current = null;
              setInMemoryArrowTable?.(null);
              setError(err instanceof Error ? err.message : String(err));
              setLoading(false);
              setParsedData(null);
              setRenderedRequestKey(load.requestKey);
              setRenderedSource(null);
            });
        }
      } else if (status === "LOADING") {
        loadGenerationRef.current += 1;
        inMemoryMatrixTableRef.current = null;
        setInMemoryArrowTable?.(null);
        setLoading(true);
        setError(null);
        setParsedData(null);
        setChunkedConfig(null);
        setRenderedRequestKey(null);
        setRenderedSource(null);
      } else if (status === "FAILED" || errorMessage) {
        loadGenerationRef.current += 1;
        console.error("[transformed view] transformation failed:", {
          status,
          errorMessage,
          transformId: effectiveTransformId,
        });
        inMemoryMatrixTableRef.current = null;
        setInMemoryArrowTable?.(null);
        setLoading(false);
        setError(errorMessage || "Transformation failed");
        setParsedData(null);
        setChunkedConfig(null);
        setRenderedRequestKey(requestKey);
        setRenderedSource(null);
      }
    },
    onError: (error: any) => {
      if (currentRequestKeyRef.current !== requestKey) return;
      // Check if this is a network error (handled gracefully by authentication service)
      const isNetworkError = error?.message?.includes("Network unavailable");

      if (isNetworkError) {
        // Network errors are handled gracefully by authentication service
        // Just show loading state and let the service handle retries
        console.warn(
          "TransformedCustomViewViewerContainer: Network error, will retry automatically",
        );
        setIsSubscriptionSkipped(true);
        retryTimeoutRef.current = setTimeout(() => {
          setIsSubscriptionSkipped(false);
        }, 5000); // 5 seconds for network errors
      } else {
        // For other errors, log and use shorter retry delay
        console.error("TransformedCustomViewViewerContainer: error", error);
        setIsSubscriptionSkipped(true);
        retryTimeoutRef.current = setTimeout(() => {
          setIsSubscriptionSkipped(false);
        }, 3000);
      }
    },
    skip: isSubscriptionSkipped || !wasmInitialized,
  });

  // Matrix transforms are optimized for rendering and can be too wide for the
  // generic date-pivot receipt contract. When a route supplies a long-form
  // receipt transform, request only its opaque receipt for the exact same run
  // and controls. Its parquet body is deliberately not parsed or displayed.
  const receiptSubscription = useGetTransformedRawParquetSubscription({
    client: perspectiveApiService.getClient(),
    variables: receiptSubscriptionVariables,
    skip:
      isSubscriptionSkipped ||
      !wasmInitialized ||
      !receiptTransformId ||
      receiptTransformId === effectiveTransformId,
  });
  useEffect(() => {
    if (
      !receiptTransformId ||
      currentReceiptRequestKeyRef.current !== receiptRequestKey
    ) {
      return;
    }
    const payload = receiptSubscription.data?.getTransformedRawParquet;
    const receiptId = payload?.renderedViewReceiptId;
    if (
      payload?.status === "COMPLETED" &&
      typeof receiptId === "string" &&
      RENDERED_VIEW_RECEIPT_ID.test(receiptId)
    ) {
      setReceiptBinding({ requestKey: receiptRequestKey, receiptId });
    } else if (
      receiptSubscription.error ||
      payload?.status === "LOADING" ||
      payload?.status === "FAILED"
    ) {
      setReceiptBinding(null);
    }
  }, [
    receiptRequestKey,
    receiptSubscription.data,
    receiptSubscription.error,
    receiptTransformId,
  ]);

  // Reset state when parameters change
  useEffect(() => {
    loadGenerationRef.current += 1;
    inMemoryMatrixTableRef.current = null;
    setInMemoryArrowTable?.(null);
    setParsedData(null);
    setChunkedConfig(null);
    setRenderedRequestKey(null);
    setRenderedSource(null);
    setLoading(true);
    setError(null);
    setSortConfig([]);
  }, [requestKey, setParsedData, setInMemoryArrowTable, setSortConfig]);

  // Extract sort keys from an in-memory arrow table by filtering rows for
  // the requested measure. Used when the matrix data arrived as base64
  // (small dataset) and the backend S3 cache may not be available.
  const extractLocalSortKeys = useCallback(
    (table: arrow.Table, measure: string): FullParsedParquetData | null => {
      const measureCol = table.getChild("measure");
      if (!measureCol) return null;

      const measureLower = measure.toLowerCase();
      const matchingIndices: number[] = [];
      for (let i = 0; i < table.numRows; i++) {
        if (String(measureCol.get(i)).toLowerCase() === measureLower) {
          matchingIndices.push(i);
        }
      }
      if (matchingIndices.length === 0) return null;

      const data: Record<string, unknown[]> = {};
      for (const field of table.schema.fields) {
        const col = table.getChild(field.name);
        if (col) {
          data[field.name] = matchingIndices.map((i) => col.get(i));
        }
      }
      const filtered = arrow.tableFromArrays(data);
      return { arrowTable: filtered, columns: filtered.schema.fields };
    },
    [],
  );

  // Build fetchSortKeys callback for the matrix path.
  // When the full data is in memory (base64), filters locally.
  // Otherwise queries the backend for a small parquet with only the
  // requested measure's rows.
  const fetchSortKeys = useCallback(
    async (measure: string): Promise<FullParsedParquetData | null> => {
      // Fast path: extract from in-memory arrow table (small dataset)
      const localTable = inMemoryMatrixTableRef.current;
      if (localTable) {
        const local = extractLocalSortKeys(localTable, measure);
        if (local) {
          console.log(
            "[fetchSortKeys] extracted locally, rows:",
            local.arrowTable.numRows,
          );
          return local;
        }
      }

      // Slow path: query the backend (large chunked dataset)
      try {
        const result = await perspectiveApiService.getClient().query({
          query: GetMatrixSortKeysDocument,
          variables: {
            datasetName,
            transformId: effectiveTransformId,
            folder,
            runSettingsJson: runSettings ?? null,
            comparedToRunSettingsJson: comparedToRunSettings ?? null,
            params: argsJson,
            measure,
          },
          fetchPolicy: "no-cache",
        });

        const b64 = result.data?.getMatrixSortKeys;
        if (!b64) {
          console.warn(
            "[fetchSortKeys] Backend returned null for measure:",
            measure,
          );
          return null;
        }

        const parsed = await parseParquetData(b64, true);
        return parsed as FullParsedParquetData;
      } catch (err) {
        console.error("[fetchSortKeys] Error:", err);
        return null;
      }
    },
    [
      extractLocalSortKeys,
      perspectiveApiService,
      datasetName,
      effectiveTransformId,
      folder,
      runSettings,
      comparedToRunSettings,
      argsJson,
    ],
  );

  // Build backend CSV export callback.  Available whenever the view uses a
  // chunkedMatrixTransformId (inventory health views) -- even when the data
  // arrived as base64 (small dataset).  The backend reads the cached parquet
  // from S3, applies sorting, and returns a signed CSV download URL.
  const backendExport: BackendExportFn | null = useMemo(() => {
    if (!chunkedConfig && !chunkedMatrixTransformId) return null;
    return async (excludeDetailRows: boolean) => {
      const sortConfigJson =
        sortConfig.length > 0
          ? sortConfig.map(({ key, direction }) => ({ key, direction }))
          : null;
      const firstSort = sortConfig[0];
      const sanitizedTitle = viewControllerConfig.title
        ? viewControllerConfig.title.replace(/[^a-z0-9]/gi, "_").toLowerCase()
        : "export";
      const runDateSuffix = activeRun?.createdAt
        ? `_${dayjs.utc(activeRun.createdAt).local().format("YYYY_MM_DD_HHmmss")}`
        : "";
      const comparedSuffix = comparedRun?.createdAt
        ? `_vs_${dayjs.utc(comparedRun.createdAt).local().format("YYYY_MM_DD_HHmmss")}`
        : "";
      const downloadFilename = `${sanitizedTitle}${runDateSuffix}${comparedSuffix}.csv`;

      const result = await perspectiveApiService.getClient().query({
        query: GetExportCsvDownloadUrlDocument,
        variables: {
          datasetName,
          transformId: effectiveTransformId,
          folder,
          runSettingsJson: runSettings ?? null,
          comparedToRunSettingsJson: comparedToRunSettings ?? null,
          params: argsJson,
          measure: params?.measure ?? null,
          excludeDetailRows,
          sortColumn: firstSort?.key ?? null,
          sortDirection: firstSort?.direction ?? null,
          sortConfig: sortConfigJson,
          downloadFilename,
        },
        fetchPolicy: "no-cache",
      });

      const downloadUrl = result.data?.getExportCsvDownloadUrl?.downloadUrl;
      if (!downloadUrl) {
        throw new Error("Backend did not return a download URL for CSV export");
      }

      // Trigger browser download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", "");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
  }, [
    chunkedConfig,
    chunkedMatrixTransformId,
    sortConfig,
    perspectiveApiService,
    datasetName,
    effectiveTransformId,
    folder,
    runSettings,
    comparedToRunSettings,
    argsJson,
    params?.measure,
    viewControllerConfig.title,
    activeRun?.createdAt,
    comparedRun?.createdAt,
  ]);

  // Register/unregister the backend export callback with the parent
  useEffect(() => {
    if (setBackendExportFn) {
      setBackendExportFn(() => backendExport);
    }
    return () => {
      if (setBackendExportFn) {
        setBackendExportFn(() => null);
      }
    };
  }, [backendExport, setBackendExportFn]);

  const currentChunkedConfig =
    chunkedConfig?.requestKey === requestKey ? chunkedConfig : null;
  const currentParsedData =
    renderedRequestKey === requestKey ? parsedData : null;
  const currentRenderedSource =
    renderedRequestKey === requestKey ? renderedSource : null;
  const displayedAcceptedSource =
    currentChunkedConfig?.renderedSource ?? currentRenderedSource ?? undefined;
  const currentReceiptId =
    receiptBinding?.requestKey === receiptRequestKey
      ? receiptBinding.receiptId
      : undefined;
  const currentAcceptedSource = receiptTransformId
    ? displayedAcceptedSource && currentReceiptId
      ? Object.freeze({
          ...displayedAcceptedSource,
          receiptId: currentReceiptId,
        })
      : undefined
    : displayedAcceptedSource;
  const onRenderedSourceChange = viewControllerConfig.onRenderedSourceChange;
  useEffect(() => {
    onRenderedSourceChange?.(currentAcceptedSource, effectiveParams);
  }, [currentAcceptedSource, effectiveParams, onRenderedSourceChange]);
  const hasStaleRenderableData = Boolean(
    (chunkedConfig && !currentChunkedConfig) ||
      (parsedData && !currentParsedData),
  );

  // Show WASM error if initialization failed
  if (wasmError) {
    return <ErrorState error={wasmError} />;
  }

  if (loading || hasStaleRenderableData) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  // PO popover host: a SIBLING of the table on purpose — its open/close/data
  // state lives in the po_cell_popover module store, so interacting with it
  // never re-renders the (virtualized) matrix table.
  const poCellPopover = (viewControllerConfig as PivotViewConfig<T>)
    ?.enablePoCellPopover ? (
    <PoCellPopover
      runSettings={runSettings ?? null}
      dateHierarchy={
        ((params as { dateHierarchy?: string } | null)?.dateHierarchy as
          | "day"
          | "week"
          | "month") ?? "week"
      }
    />
  ) : null;

  // Transfer popover host — same sibling pattern, its own module store.
  const transferCellPopover = (viewControllerConfig as PivotViewConfig<T>)
    ?.enableTransferCellPopover ? (
    <TransferCellPopover runSettings={runSettings ?? null} />
  ) : null;

  // Unfulfilled-demand popover host — same sibling pattern, its own store.
  const unfulfilledDemandCellPopover = (
    viewControllerConfig as PivotViewConfig<T>
  )?.enableUnfulfilledDemandPopover ? (
    <UnfulfilledDemandCellPopover runSettings={runSettings ?? null} />
  ) : null;

  // Forecasted-sales popover host — same sibling pattern, its own store.
  const forecastedSalesCellPopover = (
    viewControllerConfig as PivotViewConfig<T>
  )?.enableForecastedSalesPopover ? (
    <ForecastedSalesCellPopover runSettings={runSettings ?? null} />
  ) : null;

  // Sales-order popover host (Demand) — same sibling pattern, its own store.
  const salesOrderCellPopover = (viewControllerConfig as PivotViewConfig<T>)
    ?.enableSalesOrderPopover ? (
    <SalesOrderCellPopover runSettings={runSettings ?? null} />
  ) : null;

  if (currentChunkedConfig && params) {
    return (
      <>
        <ChunkedTableViewer
          key={currentChunkedConfig.requestKey}
          chunkedConfig={currentChunkedConfig}
          params={effectiveParams}
          viewControllerConfig={viewControllerConfig}
          tableType={tableType}
          columnHeaderMap={columnHeaderMap}
          columnFormatters={columnFormatters}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
          columnSchema={columnSchema}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          TableViewer={TableViewer}
          fetchSortKeys={fetchSortKeys}
          renderedSource={currentAcceptedSource}
        />
        {poCellPopover}
        {transferCellPopover}
        {unfulfilledDemandCellPopover}
        {forecastedSalesCellPopover}
        {salesOrderCellPopover}
      </>
    );
  }

  if (!currentParsedData || !params) {
    return <ErrorState error="No data available" />;
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {isChartView && chartConfig && typeof chartConfig === "function" ? (
        <ChartViewer<T>
          parsedData={currentParsedData}
          viewParams={effectiveParams}
          chartConfig={chartConfig(effectiveParams)}
        />
      ) : (
        <TableViewer<T>
          parsedData={currentParsedData}
          loading={loading}
          error={error}
          viewParams={effectiveParams}
          viewControllerConfig={viewControllerConfig}
          tableType={tableType as TableType}
          columnHeaderMap={columnHeaderMap}
          columnFormatters={columnFormatters}
          sortConfig={sortConfig}
          setSortConfig={setSortConfig}
          columnSchema={columnSchema}
          expandedGroups={expandedGroups}
          setExpandedGroups={setExpandedGroups}
          fetchSortKeys={fetchSortKeys}
          renderedSource={currentAcceptedSource}
        />
      )}
      {poCellPopover}
      {transferCellPopover}
      {unfulfilledDemandCellPopover}
      {forecastedSalesCellPopover}
      {salesOrderCellPopover}
    </Box>
  );
};
